import axios from 'axios'
import {
  GetTripTicketQry,
  TripCmd,
  GetTripTicketsCmd,
  GetTripResponse,
  GetUnassignedTicketsCmd,
  CheckEnabledManualRoutingQry,
  ManualRoutingCooldownQry,
} from '../models/Trip'
import {API_URL} from '../../../utils/common/constants/Config'
import {Response} from '../../../../setup/axios/HttpResponse'

export interface TripExportResult {
  blob: Blob
  fileName: string | null
}

const getFileNameFromContentDisposition = (disposition?: string): string | null => {
  if (!disposition) return null

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].trim())
  }

  const quotedMatch = disposition.match(/filename="([^"]+)"/i)
  if (quotedMatch?.[1]) {
    return quotedMatch[1]
  }

  const unquotedMatch = disposition.match(/filename=([^;]+)/i)
  if (unquotedMatch?.[1]) {
    return unquotedMatch[1].trim().replace(/^"|"$/g, '')
  }

  return null
}

export const TRIP_API_ROUTES = {
  GET_TRIPS: `${API_URL}/api/v1/trip/get-trips`,
  GET_TRIP_TICKETS: `${API_URL}/api/v1/trip/get-trip-tickets`,
  RE_ROUTE_TRIP: `${API_URL}/api/v1/trip/re-route-trip/:tripId`,
  GET_TRIP_CLUSTERS: `${API_URL}/api/v1/trip/get-cluster-dropdown-list`,
  EXPORT_CSV: `${API_URL}/api/v1/trip/export-trips`,
  GET_UNASSIGNED_TICKETS: `${API_URL}/api/v1/trip/get-unassigned-tickets`,
  MANUAL_ROUTE_TRIP: `${API_URL}/api/v1/trip/manual-route-trip`,
  CHECK_ENABLED_MANUAL_ROUTING: `${API_URL}/api/v1/trip/check-enabled-manual-routing`,
  GET_MANUAL_ROUTING_COOLDOWN: `${API_URL}/api/v1/trip/get-manual-routing-cooldown`,
}

/**
 * Get trips list with filters
 * Maps to: POST /api/v1/trip/get-trips
 */
export const getTrips = async (cmd: TripCmd): Promise<GetTripResponse> => {
  const response: any = await axios.post(TRIP_API_ROUTES.GET_TRIPS, cmd)

  const payload = response?.data ?? response
  const totalTrips = payload?.totalTrips ?? payload?.TotalTrips ?? 0
  const lstTrips = payload?.lstTrips ?? payload?.LstTrips ?? []

  return {
    totalTrips,
    lstTrips: Array.isArray(lstTrips) ? lstTrips : [],
  }
}

/**
 * Get tickets for a specific trip
 * Maps to: POST /api/v1/trip/get-trip-tickets
 */
export const getTripTickets = async (cmd: GetTripTicketsCmd): Promise<GetTripTicketQry[]> => {
  const response = await axios.post(TRIP_API_ROUTES.GET_TRIP_TICKETS, cmd)

  // Backend returns array directly, not wrapped in { data: [...] }
  const tickets = Array.isArray(response.data) ? response.data : response.data.data || []

  return tickets
}

export const reRoute = async (tripId: string): Promise<any> => {
  const response = await axios.post(TRIP_API_ROUTES.RE_ROUTE_TRIP.replace(':tripId', tripId))

  return response.status
}

export const getTripClusters = async (): Promise<any[]> => {
  const response = await axios.post(TRIP_API_ROUTES.GET_TRIP_CLUSTERS)

  const clusters = Array.isArray(response.data) ? response.data : response.data.data || []

  return clusters
}

const exportTripCSV = async (payload: TripCmd): Promise<TripExportResult> => {
  const response = await axios.post<Blob>(TRIP_API_ROUTES.EXPORT_CSV, payload, {
    responseType: 'blob',
    headers: {'toast-message': false},
    returnFullResponse: true,
  } as any)

  const blob = response.data

  if (blob.type?.includes('application/json')) {
    const text = await blob.text()
    const error = JSON.parse(text)
    throw new Error(error?.message || 'Export failed')
  }

  const disposition =
    response.headers?.['content-disposition'] || response.headers?.['Content-Disposition']

  return {
    blob,
    fileName: getFileNameFromContentDisposition(disposition),
  }
}

/**
 * Get tickets for a specific trip
 * Maps to: POST /api/v1/trip/get-unassigned-tickets
 */
export const getUnassignedTickets = async (
  cmd: GetUnassignedTicketsCmd
): Promise<GetTripTicketQry[]> => {
  const response = await axios.post(TRIP_API_ROUTES.GET_UNASSIGNED_TICKETS, cmd)

  // Backend returns array directly, not wrapped in { data: [...] }
  const tickets = Array.isArray(response.data) ? response.data : response.data.data || []

  return tickets
}

/**
 * Send all unassigned tickets to the routing engine.
 * Maps to: POST /api/v1/trip/manual-route-trip (no request body; tenant is in the path)
 * Returns the API envelope: { status, data, message, errorCode }
 */
export const manualRouteTrip = async (): Promise<Response<number>> => {
  // For testing cooldown, you can uncomment the following line to simulate a cooldown response
  // const response = await axios.post(TRIP_API_ROUTES.MANUAL_ROUTE_TRIP , { isTestCooldown: true})

  const response = await axios.post(TRIP_API_ROUTES.MANUAL_ROUTE_TRIP)
  // The response interceptor already unwraps the HTTP body, so `response` here
  // is the envelope itself.
  return response as unknown as Response<number>
}

/**
 * Check whether the manual-routing action is enabled, plus the current
 * unassigned-ticket count (used for the button badge).
 * Maps to: GET /api/v1/trip/check-enabled-manual-routing?clientTimeZone=...
 * Returns the API envelope: { status, data: { isEnabled, unassignedTicket }, message, errorCode }
 */
export const checkEnabledManualRouting = async (
  clientTimeZone: number
): Promise<Response<CheckEnabledManualRoutingQry>> => {
  const response = await axios.get(TRIP_API_ROUTES.CHECK_ENABLED_MANUAL_ROUTING, {
    params: { clientTimeZone },
  })

  // The response interceptor already unwraps the HTTP body, so `response`
  // here is the envelope itself.
  return response as unknown as Response<CheckEnabledManualRoutingQry>
}

/**
 * Get the current manual-routing cooldown state.
 * Maps to: GET /api/v1/trip/get-manual-routing-cooldown (tenant is in the path)
 * Returns the API envelope: { status, data: { isOnCooldown, cooldownUntil, ... }, message, errorCode }
 */
export const getManualRoutingCooldown = async (): Promise<Response<ManualRoutingCooldownQry>> => {
  const response = await axios.get(TRIP_API_ROUTES.GET_MANUAL_ROUTING_COOLDOWN)

  // The response interceptor already unwraps the HTTP body, so `response`
  // here is the envelope itself.
  return response as unknown as Response<ManualRoutingCooldownQry>
}

// Export all API functions
const TripApi = {
  getTrips,
  getTripTickets,
  reRoute,
  getTripClusters,
  exportTripCSV,
  getUnassignedTickets,
  manualRouteTrip,
  checkEnabledManualRouting,
  getManualRoutingCooldown,
}

export default TripApi
